<template>
  <v-container
    grid-list-xs
    fluid
    class="pa-2 pt-0 pb-0"
  >
    <v-container
      v-if="anytimeConsultations"
      grid-list-xs
      fluid
      class="pa-2 pt-0 pb-0"
    >
      <v-card
        :flat="true"
        class="anytime-consultation-search-info pl-2 pt-2"
      >
        <v-card-text class="row pa-0 d-flex flex-row">
          {{ order }}に表示 / {{ isLoading(listLoadState) ? "読み込み中" : `${anytimeConsultations.length}件ヒット` }}
          <span
            v-if="incompleted"
            class="ml-auto"
          >
            <v-switch
              v-model="filtered"
              base-color="grey-lighten-1"
              color="blue-accent-1"
              hide-details
              :flat="true"
              label="担当分を表示"
              class="ma-0 filtered-toggle"
              :class="anytimeConsultationFilteredFlag? 'filtered' : 'unfiltered'"
              @change="switchFilteredFlag"
            />
          </span>
        </v-card-text>
      </v-card>
      <!-- ストアには前回表示した一覧（別タブ・ログアウト前の内容）が残り得るため、
           今回の取得が完了するまでは一覧を描画せずローディングを表示する -->
      <div
        v-if="isLoading(listLoadState)"
        class="d-flex justify-center pa-6"
        data-testid="list-loading"
      >
        <v-progress-circular
          indeterminate
          color="grey"
        />
      </div>
      <v-container
        v-else
        fluid
      >
        <div
          v-for="consultation in anytimeConsultations"
          :key="consultation.id"
          class="anytime-consultation-info"
          :class="showIncentiveNotice(consultation) ? 'over-deadline-incentive' : ''"
        >
          <div
            v-if="showIncentiveNotice(consultation)"
            class="incentive-notice ma-2"
          >
            <v-icon
              class="notice-icon"
              color="white"
              size="30px"
            >
              mdi-alert-circle-outline
            </v-icon>
            他の相談員が期限内に回答できませんでした。代わりに回答をお願いいたします。インセンティブをお支払いします。
          </div>
          <anytimeConsultationDetail
            :anytime-consultation="consultation"
            :tab-id="tabId"
            :incentive="showIncentiveNotice(consultation)"
            :show-countdown="incompleted"
            :now-ms="nowMs"
          />
        </div>

        <!-- 担当分を表示 -->
        <!-- 未回答 -->
        <!-- 担当科目 -->
        <div v-if="anytimeConsultationFilteredFlag && incompleted && currentDepartmentType">
          <!-- 一覧・受付時間中か・勤務完了の表示期限がすべて最新の取得結果で揃うまでは、
               前回の値や取得途中の空の一覧で誤ったステータスを表示しないようローディングを表示する -->
          <div
            v-if="isLoading(statusLoadState)"
            class="d-flex justify-center pa-6"
            data-testid="status-notice-loading"
          >
            <v-progress-circular
              indeterminate
              color="grey"
            />
          </div>
          <template v-else-if="isSuccess(statusLoadState)">
            <!-- 受付時間中 -->
            <div v-if="onShift">
              <anytimeConsultationAnswerNotice
                bg-color="#F9E5EC"
                text-color="#E00051"
                icon="mdi-alert-circle-outline"
                title="受付時間中"
                detail="相談が増える可能性があります。"
              />
            </div>
            <!-- 受付終了 -->
            <div v-else-if="anytimeConsultations.length > 0">
              <anytimeConsultationAnswerNotice
                bg-color="#D7D7D7"
                text-color="#8A8A8A"
                icon="mdi-bell-outline"
                title="受付終了"
                detail="これ以上相談は増えません。"
              />
            </div>
            <!-- 勤務完了 -->
            <div v-else-if="noticeExpiration">
              <anytimeConsultationAnswerNotice
                bg-color="#ECF7E4"
                text-color="#69B72E"
                icon="mdi-emoticon-happy-outline"
                title="勤務完了"
                detail="ご担当ありがとうございました。"
              />
            </div>
          </template>
        </div>
      </v-container>
    </v-container>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AnytimeConsultationDetail from "./shared/AnytimeConsultationDetailCard.vue";
import AnytimeConsultationAnswerNotice from "./shared/ac_shift/AnytimeConsultationAnswerNotice.vue";
import loadStateMixin from "../mixins/load_state.js";
import { LOAD_STATES } from "../constants/load_states.js";
import { findAnytimeConsultationListTab } from "../entities/anytime-consultation-list-tab";

const COUNTDOWN_INTERVAL_MS = 60 * 1000;
const LIST_ROUTE_NAME = "anytimeConsultationList";
const DEPARTMENT_TYPES = {
  "1": "pediatrician",
  "2": "obstetrician",
  "3": "midwife",
};

export default {
  name: "AnytimeConsultationList",
  components: {
    AnytimeConsultationDetail,
    AnytimeConsultationAnswerNotice
  },
  mixins: [loadStateMixin],
  data: () => ({
    tabId: 0,
    order: "古い順",
    incompleted: true,
    departmentType: null,
    filtered: true,
    nowMs: Date.now(),
    countdownTimerId: null,
    departmentTypes: DEPARTMENT_TYPES,
    currentDepartmentType: false,
    currentDoctorLoaded: false,
    isComponentUnmounted: false,
    // 一覧の取得状態
    listLoadState: LOAD_STATES.LOADING,
    // 受付ステータスバーの表示に必要な、一覧・受付時間中か・勤務完了の表示期限の取得状態
    statusLoadState: LOAD_STATES.LOADING,
    latestRefreshId: 0,
  }),
  computed: {
    ...mapGetters([
      "anytimeConsultations",
      "currentDoctor",
      "anytimeConsultationFilteredFlag",
      "onShift",
      "noticeExpiration",
      "hasAllCapabilities"
    ]),
    counselorDoctor: function () {
      return this.hasAllCapabilities(["counselor:access"]);
    },
    operatorDoctor: function () {
      return this.hasAllCapabilities(["operator:access"]);
    },
  },
  methods: {
    ...mapActions([
      "fetchAnytimeConsultations",
      "fetchAssignedAnytimeConsultations",
      "fetchCompletedAnytimeConsultations",
      "resetAnytimeConsultationsList",
      "assignFilteredFlag",
      "fetchCurrentDoctor",
      "isOnAnytimeConsultationShift",
      "isAnswerNoticeExpiration"
    ]),
    // 一覧・受付時間中か・勤務完了の表示期限をまとめて取り直す。
    // 一覧は今回の取得が完了するまで、受付ステータスバーは3つの取得がすべて今回の呼び出しの結果で揃うまで、
    // それぞれローディング表示にする
    refresh: function () {
      // 取得できない場合も含めて世代を進め、実行中の古い refresh の結果で表示しないようにする
      const refreshId = ++this.latestRefreshId;
      this.statusLoadState = LOAD_STATES.LOADING;
      // 一覧以外のルートへの遷移中（フェードアウト中）は、表示中の一覧をそのまま残す
      if (this.$route.name === LIST_ROUTE_NAME) {
        this.applyRouteQuery();
        this.listLoadState = LOAD_STATES.LOADING;
      }
      if (!this.canRefresh()) return;

      // より新しい refresh が始まっている場合は、そちらの結果で状態を決める
      const isLatestRefresh = () => refreshId === this.latestRefreshId && !this.isComponentUnmounted;
      // false は古いレスポンスとして破棄されたかAPIエラー
      const toLoadState = (applied) => (applied ? LOAD_STATES.SUCCESS : LOAD_STATES.ERROR);

      const listRequest = this.loadList().then((applied) => {
        if (isLatestRefresh()) this.listLoadState = toLoadState(applied);
        return applied;
      });
      Promise.all([
        listRequest,
        this.isOnShift(),
        this.isNoticeExpiration()
      ]).then((results) => {
        if (!isLatestRefresh()) return;

        // 誤った表示を避けるため、どれか1つでも反映されなかった場合はステータスを出さない
        this.statusLoadState = toLoadState(results.every(Boolean));
      });
    },
    canRefresh: function () {
      if (this.isComponentUnmounted || !this.currentDoctorLoaded) return false;
      // 詳細画面への遷移中などに一覧以外のクエリで取得しないようにする
      if (this.$route.name !== LIST_ROUTE_NAME) return false;

      // tab_id のみのクエリは AnytimeConsultationHome が department / complete を補って再遷移するため、
      // 不完全なクエリでは取得せず（部門未指定での400エラーや二重取得を防ぐ）、補完後の再実行に任せる
      const { department, complete } = this.$route.query;
      return department !== undefined && complete !== undefined;
    },
    // 表示モード（未回答/回答済・並び順・診療科）を URL から決める。
    // 取得開始まで初期値（未回答）のままだと、回答済タブでも未回答用の表示になるため、表示開始時から反映する
    applyRouteQuery: function () {
      const { tab_id: tabId } = this.$route.query;
      // tab_id のみのクエリ（AnytimeConsultationHome による補完前）でもタブ定義から表示モードを決める
      const tab = findAnytimeConsultationListTab(tabId);
      const complete = this.$route.query.complete ?? tab?.complete;
      const department = this.$route.query.department ?? tab?.department;

      this.tabId = tabId;
      this.incompleted = complete == 0;
      this.order = this.incompleted ? "古い順" : "新しい順";
      this.departmentType = department === undefined ? undefined : String(department);
      this.currentDepartmentType = (this.departmentType in this.departmentTypes && this.departmentTypes[this.departmentType] === this.currentDoctor?.department_type);
    },
    loadList: function () {
      if(this.incompleted){
        return this.loadIncompletedList();
      }
      // 完了済
      return this.fetchCompletedAnytimeConsultations({
        department: this.departmentType
      });
    },
    loadIncompletedList: function() {
      // 未完了
      if((this.counselorDoctor || this.operatorDoctor ) && this.filtered){
        // アサインされた相談のみ
        return this.fetchAssignedAnytimeConsultations({
          department: this.departmentType
        });
      }
      // すべての相談
      return this.fetchAnytimeConsultations({
        department: this.departmentType
      });
    },
    switchFilteredFlag: function() {
      this.assignFilteredFlag(this.filtered);
      this.refresh();
    },
    isOnShift: function () {
      return this.isOnAnytimeConsultationShift({
        doctor_id: this.currentDoctor.id
      });
    },
    isNoticeExpiration: function () {
      return this.isAnswerNoticeExpiration({
        doctor_id: this.currentDoctor.id
      });
    },
    isNoRemainingTime: function (consultation) {
      const deadlineAtIso = consultation?.deadlineAtIso;
      if (!deadlineAtIso) return false;

      const deadlineMs = Date.parse(deadlineAtIso);
      if (Number.isNaN(deadlineMs)) return false;

      const remainingMs = deadlineMs - this.nowMs;
      return remainingMs <= 0;
    },
    showIncentiveNotice: function (consultation) {
      return this.incompleted && this.filtered && (consultation.overDeadline || this.isNoRemainingTime(consultation));
    }
  },
  watch: {
    "$route.query" () {
      this.refresh();
    }
  },
  created() {
    // 前回表示した一覧が描画されないよう、取得開始（fetchCurrentDoctor の完了後）を待たずに消しておく
    this.resetAnytimeConsultationsList();
    this.applyRouteQuery();
    // fetchCurrentDoctor 完了前のルート変更は refresh 側で無視し、完了後に最新のクエリで1回だけ取得する
    this.fetchCurrentDoctor().then(() => {
      if (this.isComponentUnmounted) return;

      if(this.counselorDoctor || this.operatorDoctor){
        this.filtered = this.anytimeConsultationFilteredFlag;
      } else {
        this.filtered = false;
      }
      this.currentDoctorLoaded = true;
      this.refresh();
    });
  },
  mounted() {
    this.nowMs = Date.now();
    this.countdownTimerId = setInterval(() => {
      this.nowMs = Date.now();
    }, COUNTDOWN_INTERVAL_MS);
  },
  beforeUnmount() {
    this.isComponentUnmounted = true;
    if (this.countdownTimerId) {
      clearInterval(this.countdownTimerId);
      this.countdownTimerId = null;
    }
  },
};
</script>

<style lang="scss">
  .anytime-consultation-search-info {
    background-color: #fafafa;
    margin: 2px;

    .v-card__text {
      background-color: #fafafa;
    }
  }

  .anytime-consultation-info {
    border: solid 1px #888888;
    background-color: white;
    border-radius: 2px;

    .v-card {
      border: solid 1px #bdbdbd !important;
      height: 100%;
    }
  }
  .over-deadline-incentive{
    background-color: #E00051!important;

    div.incentive-notice {
      font-weight: bold;
      font-size: 14px;
      color: white;

      .notice-icon {
        width: 30px;
        height: 30px;
      }
    }
  }
  .filtered {
    label {
      color: #212121 !important;
    }
  }
  .unfiltered {
    label {
      color: #ccc !important;
    }
  }
  .v-switch .v-selection-control {
    min-height: 0px !important;
  }
  .v-switch .v-selection-control--density-default {
    --v-selection-control-size: auto !important;
  }
  .v-switch .v-selection-control > label {
    margin-bottom: 0px !important;
  }
</style>
